import { TableTextFilter } from "components/TableTextFilter/TableTextFilter";
import { setScrollBehavior } from "features/scrollPageBehaviorProvider/scrollPageBehaviorProvider";
import { _ } from "i18n";
import type { PlayerPossibleDuplicateRowResponse } from "libs/apiClient";
import client from "libs/client";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useI18n } from "features/translation/context";
import { getDateFormat } from "ui-kit/src/functions";
import { Link } from "react-router";
import DatagridV2, { type SimpleColumn } from "ui-kit/src/Datagrid/DatagridV2";
import { useToast } from "ui-kit";
import Modal from "ui-kit/src/Modal";
import { Button } from "../../../ui-kit-v2/src/button/button";
import { DateTimeRangeCalendar } from "ui-kit/src/DateTimeRangeCalendar/DateTimeRangeCalendar";
import { PlayerData } from "./productManagement/types";
import Migrate from "./productManagement/nonPartnerManagement/migrate/Migrate";
import { MigrationType } from "./productManagement/types";
import { SortDirection } from "types";

export enum PlayerPossibleDuplicateStatus {
	NEW = "new",
	MIGRATED = "migrated",
	NOT_DUPLICATE = "not_duplicate",
	SKIPPED = "skipped",
	MERGED = "merged",
	RETAINED = "retained",
}

const RESOLVED_STATUSES: readonly PlayerPossibleDuplicateStatus[] = [
	PlayerPossibleDuplicateStatus.MIGRATED,
	PlayerPossibleDuplicateStatus.NOT_DUPLICATE,
	PlayerPossibleDuplicateStatus.SKIPPED,
	PlayerPossibleDuplicateStatus.MERGED,
	PlayerPossibleDuplicateStatus.RETAINED,
];

export enum ResolutionFilter {
	ALL = "all",
	PENDING = "pending",
	PROCESSED = "processed",
}

function isRowResolved(
	row: PlayerPossibleDuplicateRowResponse | null | undefined,
): boolean {
	return Boolean(
		(row?.status && RESOLVED_STATUSES.includes(row.status)) ||
			row?.markedAsNotDuplicate,
	);
}

const STATUS_TRANSLATION_KEY_MAP: Record<string, string> = {
	[PlayerPossibleDuplicateStatus.MIGRATED]: "admin.duplicate_page.status.migrated",
	[PlayerPossibleDuplicateStatus.NOT_DUPLICATE]: "admin.duplicate_page.status.not_duplicate",
	[PlayerPossibleDuplicateStatus.SKIPPED]: "admin.duplicate_page.status.skipped",
	[PlayerPossibleDuplicateStatus.MERGED]: "admin.duplicate_page.status.merged",
	[PlayerPossibleDuplicateStatus.RETAINED]: "admin.duplicate_page.status.retained",
};

function getStatusTranslationKey(
	row: PlayerPossibleDuplicateRowResponse | null | undefined,
): string {
	const status = row?.status ?? null;
	if (row?.markedAsNotDuplicate) {
		return "admin.duplicate_page.status.not_duplicate";
	}
	if (status === null || status === undefined) {
		return "admin.duplicate_page.status.new";
	}
	return STATUS_TRANSLATION_KEY_MAP[status] ?? "admin.duplicate_page.status.new";
}

/** Merge needs the club player id of the “duplicate” side (`PlayerData.id`); `playerId` alone is the other record. */
function canMergeDuplicateRow(
	row: PlayerPossibleDuplicateRowResponse,
): boolean {
	return row.playerId != null && row.possibleDuplicateId != null;
}

/** Partner players use `clubId` for Migrate; non-partner uses `clubNonpartnerId` (same DB FK, different API paths). */
function rowToPlayerData(
	row: PlayerPossibleDuplicateRowResponse,
): PlayerData {
	const rawClubId =
		row.clubId !== undefined &&
		row.clubId !== null &&
		row.clubId !== "" &&
		!Number.isNaN(parseInt(String(row.clubId), 10))
			? parseInt(String(row.clubId), 10)
			: undefined;

	const isPartner = row.playerEntityType === "Partner";

	return {
		id: row.possibleDuplicateId ?? undefined,
		firstName: row.playerFirstName ?? undefined,
		lastName: row.playerLastName ?? undefined,
		clubNonpartnerId:
			!isPartner && rawClubId !== undefined
				? String(rawClubId)
				: row.clubNonpartnerId &&
					  !Number.isNaN(parseInt(row.clubNonpartnerId, 10))
					? String(parseInt(row.clubNonpartnerId, 10))
					: undefined,
		clubId: isPartner ? rawClubId : undefined,
		npClubName: row.npClubName ?? undefined,
		npClubCountry: row.npClubCountry ?? undefined,
	};
}

export function DuplicatePlayersPage({
	countries,
}: { countries: { [key: string]: string } }) {
	const [pagination, setPagination] = useState<{
		pageIndex: number;
		pageSize: number;
	}>({
		pageIndex: 0,
		pageSize: 15,
	});
	const [filters, setFilters] = useState<{
		[key: string]: string | number | string[] | undefined;
	}>({});
	const [filterValues, setFilterValues] = useState<{
		[key: string]: string | undefined;
	}>({});
	const [createdAtTimeRange, setCreatedAtTimeRange] = useState({ start: "", end: "" });
	const [birthdayTimeRange, setBirthdayTimeRange] = useState({ start: "", end: "" });
	const [sort, setSort] = useState<{ field: string; direction: string }>();
	const [showNotDuplicateModal, setShowNotDuplicateModal] = useState(false);
	const [selectedRow, setSelectedRow] = useState<
		PlayerPossibleDuplicateRowResponse | undefined
	>(undefined);
	const [notesInput, setNotesInput] = useState("");
	const { addSuccessToast, addErrorToast } = useToast();
	const [selectedPlayer, setSelectedPlayer] = useState<PlayerData | undefined>(
		undefined,
	);
	const [showMigrate, setShowMigrate] = useState(false);
	const [partnerType, setPartnerType] = useState<MigrationType>(
		MigrationType.PARTNER,
	);
	const { locale } = useI18n();
	const [resolutionFilter, setResolutionFilter] = useState<ResolutionFilter>(
		ResolutionFilter.PENDING,
	);

	useEffect(() => {
		setScrollBehavior();
	}, []);

	const rawStatusFilter = filters?.status;
	const statusFilterActive = (() => {
		if (rawStatusFilter === undefined || rawStatusFilter === "") {
			return false;
		}
		if (Array.isArray(rawStatusFilter)) {
			return rawStatusFilter.some(
				(v) => typeof v === "string" && v.length > 0,
			);
		}
		return true;
	})();

	/** Status column reflects explicit `filters.status`; when unset, mirror resolution preset so UI matches the request. */
	const statusSelectValue =
		statusFilterActive
			? Array.isArray(rawStatusFilter)
				? rawStatusFilter.find((v) => typeof v === "string" && v.length > 0) ?? ""
				: String(rawStatusFilter)
			: resolutionFilter === ResolutionFilter.PENDING
				? PlayerPossibleDuplicateStatus.NEW
				: resolutionFilter === ResolutionFilter.PROCESSED
					? ResolutionFilter.PROCESSED
					: "";

	const { data, isLoading, refetch } =
		client.getPlayerPossibleDuplicates.useQuery({
			page: pagination.pageIndex + 1,
			limit: pagination.pageSize,
			filters: [
				...(!statusFilterActive && resolutionFilter === ResolutionFilter.PENDING
					? [
							{
								field: "status",
								type: "exact",
								value: PlayerPossibleDuplicateStatus.NEW,
							},
					  ]
					: []),
				...(!statusFilterActive && resolutionFilter === ResolutionFilter.PROCESSED
					? [
							{
								field: "status",
								type: "inarray",
								value: RESOLVED_STATUSES
							},
					  ]
					: []),
				...(filters?.possibleDuplicateId
					? [
							{
								field: "possibleDuplicateId",
								type: "exact",
								value: filters.possibleDuplicateId,
							},
					  ]
					: []),
				...(filters?.playerFirstName != null &&
				String(filters.playerFirstName).trim() !== ""
					? [
							{
								field: "playerFirstName",
								type: "search",
								value: String(filters.playerFirstName).trim(),
							},
					  ]
					: []),
				...(filters?.playerLastName != null &&
				String(filters.playerLastName).trim() !== ""
					? [
							{
								field: "playerLastName",
								type: "search",
								value: String(filters.playerLastName).trim(),
							},
					  ]
					: []),
				...(statusFilterActive && rawStatusFilter !== undefined
					? (() => {
							const values = Array.isArray(rawStatusFilter)
								? rawStatusFilter.filter(
										(v): v is string => typeof v === "string" && v.length > 0,
								  )
								: typeof rawStatusFilter === "string" && rawStatusFilter.length > 0
									? [rawStatusFilter]
									: [];
							if (values.length === 0) {
								return [];
							}
							if (values.length === 1) {
								return [
									{
										field: "status",
										type: "exact",
										value: values[0],
									},
								];
							}
							return [
								{
									field: "status",
									type: "inarray",
									value: values,
								},
							];
					  })()
					: []),
				...(filters?.notes
					? [
						{
							field: "notes",
							type: "search",
							value: filters.notes,
						},
					]
					: []),
				...((createdAtTimeRange.start || createdAtTimeRange.end)
					? [
							{
								field: "createdAt",
								type: "datetimerange",
								value: {
									start:
										createdAtTimeRange.start === ""
											? undefined
											: createdAtTimeRange.start,
									end:
										createdAtTimeRange.end === ""
											? undefined
											: createdAtTimeRange.end,
								},
							},
					  ]
					: []),
				...((birthdayTimeRange.start || birthdayTimeRange.end)
					? [
							{
								field: "birthday",
								type: "datetimerange",
								value: {
									start:
										birthdayTimeRange.start === ""
											? undefined
											: birthdayTimeRange.start,
									end:
										birthdayTimeRange.end === ""
											? undefined
											: birthdayTimeRange.end,
								},
							},
					  ]
					: []),
			],
			order: sort,
		});

	const updateStatusMutation = client.patchPlayerPossibleDuplicate.useMutation({
		onSuccess: () => {
			addSuccessToast(_("message.status.update.success"));
			client.queryClient.invalidateQueries({
				predicate: (query) =>
					query.queryKey.includes("getPlayerPossibleDuplicates"),
			});
			setShowNotDuplicateModal(false);
			setNotesInput("");
			setSelectedRow(undefined);
		},
		onError: (error) => {
			addErrorToast(error.message);
		},
	});

	const handleSetFilter = useCallback((key: string, value?: number | string | string[]) => {
		setFilters((prev) => ({ ...prev, [key]: value }));
		setPagination((p: { pageIndex: number; pageSize: number }) => ({
			...p,
			pageIndex: 0,
		}));
	}, []);

	const handleClearFilter = useCallback((key: string) => {
		setFilters((prev) => ({ ...prev, [key]: undefined }));
		setPagination((p: { pageIndex: number; pageSize: number }) => ({
			...p,
			pageIndex: 0,
		}));
	}, []);

	const handleCreatedAtTimeRangeChange = useCallback((start: string, end: string) => {
		setCreatedAtTimeRange({ start, end });
		setPagination((p: { pageIndex: number; pageSize: number }) => ({
			...p,
			pageIndex: 0,
		}));
	}, []);

	const handleBirthdayTimeRangeChange = useCallback((start: string, end: string) => {
		setBirthdayTimeRange({ start, end });
		setPagination((p: { pageIndex: number; pageSize: number }) => ({
			...p,
			pageIndex: 0,
		}));
	}, []);

	const toggleSortOrder = useCallback((name: string) => {
		setSort((prevSort) => {
			let newOrder: SortDirection.ASC | SortDirection.DESC | null = SortDirection.DESC;

			if (prevSort?.field === name) {
				if (prevSort.direction === SortDirection.DESC) {
					newOrder = SortDirection.ASC;
				} else if (prevSort.direction === SortDirection.ASC) {
					newOrder = null;
				}
			}

			if (newOrder !== null) {
				return { field: name, direction: newOrder };
			}
			return undefined;
		});
	}, []);

	const formatDateTime = useCallback(
		(dateString: string | null | undefined) => {
			if (!dateString) return "-";
			try {
				const date = new Date(dateString);
				return new Intl.DateTimeFormat(getDateFormat(locale) ?? "en-GB", {
					dateStyle: "short",
					timeStyle: "short",
				}).format(date);
			} catch {
				return "-";
			}
		},
		[locale],
	);

	const handleMarkAsNotDuplicateClick = useCallback(
		(row: PlayerPossibleDuplicateRowResponse) => {
			setSelectedRow(row);
			setShowNotDuplicateModal(true);
		},
		[],
	);

	const handleMarkAsNotDuplicateSubmit = async () => {
		if (!selectedRow) {
			addErrorToast(_("duplicate.error.no_row_selected"));
			return;
		}

		try {
			await updateStatusMutation.mutateAsync({
				id: selectedRow.id,
				body: {
					status: PlayerPossibleDuplicateStatus.NOT_DUPLICATE,
					notes: notesInput,
				},
			});
		} catch {
			// Error toast from updateStatusMutation.onError
		}
	};

	const handleCancelModal = () => {
		setShowNotDuplicateModal(false);
		setNotesInput("");
		setSelectedRow(undefined);
	};

	const handleMerge = useCallback((row: PlayerPossibleDuplicateRowResponse) => {
		if (!canMergeDuplicateRow(row)) {
			return;
		}
		setSelectedPlayer(rowToPlayerData(row));
		setPartnerType(
			row.playerEntityType === "Partner"
				? MigrationType.PARTNER
				: MigrationType.NON_PARTNER,
		);
		setShowMigrate(true);
	}, []);

	const simpleColumns: SimpleColumn<PlayerPossibleDuplicateRowResponse>[] =
		useMemo(() => {
			const generateSortArrows = (columnName: string) => (
				<div
					className="sortArrows"
					onClick={() => toggleSortOrder(columnName)}
				>
					{sort?.field === columnName ? (
						sort.direction === SortDirection.ASC ? (
							<span className="icon-new icon-16 icon-select-arrow-down up" />
						) : (
							<span className="icon-new icon-16 icon-select-arrow-down down" />
						)
					) : (
						<>
							<span className="icon-new icon-16 icon-select-arrow-down up" />
							<span className="icon-new icon-16 icon-select-arrow-down down" />
						</>
					)}
				</div>
			);

			const generateColumnHeader =
				(
					columnName: string,
					label: string,
					isNumber?: boolean,
					sortable: boolean = false,
				) =>
				() =>
					(
						<div className="tableHeader">
							<TableTextFilter
								value={filterValues[columnName] || ""}
								onChange={(value) =>
									setFilterValues((prev) => ({
										...prev,
										[columnName]: value,
									}))
								}
								number={isNumber}
								clear={() => {
									handleClearFilter(columnName);
									setFilterValues((prev) => ({
										...prev,
										[columnName]: undefined,
									}));
								}}
								onSubmit={(value: string) =>
									handleSetFilter(columnName, value)
								}
								placeholder={label}
							/>
							{sortable && generateSortArrows(columnName)}
						</div>
					);

			return [
				{
					id: "actions",
					key: "actions",
					header: () => <div className="tableHeader">{_("app.player.game_page.content.actions.title")}</div>,
					cell: (value, row) => {
						const isDisabled = isRowResolved(row);
						const mergeDisabled = isDisabled || !canMergeDuplicateRow(row);
						return (
							<div className="actionsCell">
								<Button
									variant="primary"
									disabled={isDisabled}
									onClick={() => handleMarkAsNotDuplicateClick(row)}
									className={clsx("btn btn--grey", isDisabled && "disabled")}
								>
									{_("admin.duplicate_page.status.not_duplicate")}
								</Button>
								&nbsp;
								<Button
									variant="secondary"
									disabled={mergeDisabled}
									onClick={() => handleMerge(row)}
									className={clsx("btn btn-primary", mergeDisabled && "disabled")}
								>
									{_("admin.duplicate_page.label.merge")}
								</Button>
							</div>
						);
					},
				},
				{
					id: "status",
					key: "status",
					header: () => (
						<div className="tableHeader" style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
							<select
								value={statusSelectValue}
								onChange={(e) => {
									const value = e.target.value;
									if (value === ResolutionFilter.PROCESSED) {
										handleSetFilter("status", undefined);
										setFilterValues((prev) => ({ ...prev, status: undefined }));
										setResolutionFilter(ResolutionFilter.PROCESSED);
										return;
									}
									handleSetFilter("status", value || undefined);
									setFilterValues((prev) => ({ ...prev, status: value || undefined }));
									setResolutionFilter(ResolutionFilter.ALL);
								}}
								style={{
									padding: "0.35rem 0.5rem",
									borderRadius: "4px",
									border: "1px solid #ccc",
									fontSize: "inherit",
									cursor: "pointer",
									width: "100%",
									minWidth: "120px",
								}}
							>
								<option value="">{_("admin.duplicate_page.filter.all")}</option>
								<option value={ResolutionFilter.PROCESSED}>{_("admin.duplicate_page.filter.processed")}</option>
								<option value={PlayerPossibleDuplicateStatus.NEW}>{_("admin.duplicate_page.status.new")}</option>
								<option value={PlayerPossibleDuplicateStatus.MIGRATED}>{_("admin.duplicate_page.status.migrated")}</option>
								<option value={PlayerPossibleDuplicateStatus.NOT_DUPLICATE}>{_("admin.duplicate_page.status.not_duplicate")}</option>
								<option value={PlayerPossibleDuplicateStatus.SKIPPED}>{_("admin.duplicate_page.status.skipped")}</option>
								<option value={PlayerPossibleDuplicateStatus.MERGED}>{_("admin.duplicate_page.status.merged")}</option>
								<option value={PlayerPossibleDuplicateStatus.RETAINED}>{_("admin.duplicate_page.status.retained")}</option>
							</select>
						</div>
					),
					cell: (value, row) => (
						<div className="thinCell">{_(getStatusTranslationKey(row) as Parameters<typeof _>[0])}</div>
					),
				},
				{
					id: "possibleDuplicateId",
					key: "possibleDuplicateId",
					header: generateColumnHeader(
						"possibleDuplicateId",
						_("admin.duplicate_page.label.possible_duplicate_id"),
						true,
					),
					cell: (value, row) => (
						<div className="thinCell">
							{row.migrationSourcePlayerId ? (
								<Link
									to={
										!row.possibleDuplicateId
											? "#"
											: `/discover/player-data/${row.migrationSourcePlayerId}`
									}
									target="_blank"
									className="button-link"
								>
									{row.migrationSourcePlayerId}
									{!row.possibleDuplicateId
										? ` (${_("admin.duplicate_page.label.deleted")})`
										: ""}
								</Link>
							) : row.possibleDuplicateId ? (
								<Link
									to={`/discover/player-data/${row.possibleDuplicateId}`}
									target="_blank"
									className="button-link"
								>
									{row.possibleDuplicateId}
								</Link>
							) : (
								"-"
							)}
						</div>
					),
				},
				{
					id: "playerId",
					key: "playerId",
					header: () => (
						<div className="tableHeader">
							{_("admin.duplicate_page.label.player_id")}
						</div>
					),
					cell: (value, row) => (
						<div className="thinCell">
							{row.migrationTargetPlayerId ? (
								<Link
									to={
										!row.playerId
											? ""
											: `/discover/player-data/${row.migrationTargetPlayerId}`
									}
									target="_blank"
									className="button-link"
								>
									{row.migrationTargetPlayerId ?? "-"}
									{!row.playerId
										? ` (${_("admin.duplicate_page.label.deleted")})`
										: ""}
								</Link>
							) : row.playerId ? (
								<Link
									to={`/discover/player-data/${row.playerId}`}
									target="_blank"
									className="button-link"
								>
									{row.playerId}
								</Link>
							) : (
								"-"
							)}
						</div>
					),
				},
				{
					id: "playerFirstName",
					key: "playerFirstName",
					header: generateColumnHeader(
						"playerFirstName",
						_("app.player.profile.first_name.title"),
						false,
						true,
					),
					cell: (value, row) => row.playerFirstName ?? "-",
				},
				{
					id: "playerLastName",
					key: "playerLastName",
					header: generateColumnHeader(
						"playerLastName",
						_("app.player.profile.last_name.title"),
						false,
						true,
					),
					cell: (value, row) => (
						<div className="thinCell">{row.playerLastName ?? "-"}</div>
					),
				},
				{
					id: "birthday",
					key: "birthday",
					header: () => (
						<div className="tableHeader tableHeader--calendar-left">
							<DateTimeRangeCalendar
								placeholder={_("label.player.birthday")}
								apiFormat
								onChange={handleBirthdayTimeRangeChange}
								start={birthdayTimeRange.start}
								end={birthdayTimeRange.end}
								locale={locale}
							/>
						</div>
					),
					cell: (value, row) => (
						<div className="thinCell">{row.birthday ?? "-"}</div>
					),
				},
				{
					id: "nationality",
					key: "nationality",
					header: () => (
						<div className="tableHeader">
							{_("label.player.nationality")}
						</div>
					),
					cell: (value, row) => (
						<div className="thinCell">
							{[row.nationality, row.nationality2]
								.filter(Boolean)
								.join(" / ") || "-"}
						</div>
					),
				},
				{
					id: "notes",
					key: "notes",
					header: generateColumnHeader("notes", _("label.player.notes")),
					cell: (value, row) => (
						<div className="thinCell">{row.notes ?? "-"}</div>
					),
				},
				{
					id: "createdAt",
					key: "createdAt",
					header: () => (
						<div className="tableHeader tableHeader--calendar-anchor-end">
							<DateTimeRangeCalendar
								placeholder={_("label.created_date")}
								apiFormat
								onChange={handleCreatedAtTimeRangeChange}
								start={createdAtTimeRange.start}
								end={createdAtTimeRange.end}
								locale={locale}
							/>
							{generateSortArrows("createdAt")}
						</div>
					),
					cell: (value, row) => (
						<div className="thinCell">{formatDateTime(row.createdAt ?? null)}</div>
					),
				},
			];
		}, [
			sort,
			filterValues,
			formatDateTime,
			handleClearFilter,
			handleSetFilter,
			handleBirthdayTimeRangeChange,
			handleCreatedAtTimeRangeChange,
			toggleSortOrder,
			handleMarkAsNotDuplicateClick,
			handleMerge,
			locale,
			birthdayTimeRange,
			createdAtTimeRange,
			statusSelectValue,
		]);

	return (
		<div className="signupRequestsPage-container">
			{!showMigrate && (
				<>
					<div
						style={{
							display: "flex",
							justifyContent: "left",
							alignItems: "center",
							marginBottom: "0.5rem",
							gap: "1rem",
							flexWrap: "wrap",
						}}
					>
						<div className="pageTitle">{_("product.management.admin.user.duplicates")}</div>
						<select
							value={resolutionFilter}
							onChange={(e) => {
								setResolutionFilter(e.target.value as ResolutionFilter);
								setPagination((p: { pageIndex: number; pageSize: number }) => ({
									...p,
									pageIndex: 0,
								}));
								setFilters((prev) => ({ ...prev, status: undefined }));
								setFilterValues((prev) => ({ ...prev, status: undefined }));
							}}
							style={{
								padding: "0.5rem 0.75rem",
								borderRadius: "4px",
								border: "1px solid #ccc",
								fontSize: "inherit",
								cursor: "pointer",
							}}
							disabled={statusFilterActive}
						>
							<option value={ResolutionFilter.ALL}>{_("admin.duplicate_page.filter.all")}</option>
							<option value={ResolutionFilter.PENDING}>{_("admin.duplicate_page.filter.pending")}</option>
							<option value={ResolutionFilter.PROCESSED}>{_("admin.duplicate_page.filter.processed")}</option>
						</select>
						<div
							className="refetchButton"
							onClick={() => refetch()}
							style={{ cursor: "pointer" }}
						>
							<span className="icon-new icon-admin-syc icon-white icon-16" />
							<div className="buttonText">{_("label.refresh")}</div>
						</div>
					</div>
					<DatagridV2
						isLoading={isLoading}
						simpleColumns={simpleColumns}
						data={data?.data.items ?? []}
						pageCount={data?.data?.pages ?? 1}
						pagination={pagination}
						setPagination={setPagination}
						noResults={_("datatables.zero_records")}
					/>
				</>
			)}

			{showMigrate && selectedPlayer && (
				<Migrate
					club={null}
					player={selectedPlayer}
					countries={countries}
					onClose={() => {
						setShowMigrate(false);
						// Invalidate queries to refresh the duplicates list after migration
						// The backend updates the PossiblePlayerDuplicate status via checkAndUpdatePossibleDuplicates
						client.queryClient.invalidateQueries({
							predicate: (query) =>
								query.queryKey.includes("getPlayerPossibleDuplicates"),
						});
					}}
					type={MigrationType.ANY}
					partnerType={partnerType}
				/>
			)}

			<Modal
				show={showNotDuplicateModal}
				onClose={handleCancelModal}
				size="sm"
				title={_("admin.duplicate_page.btn.mark_as_not_duplicate")}
			>
				<div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
					<div>
						<label
							htmlFor="notes"
							style={{ display: "block", marginBottom: "0.5rem" }}
						>
							{_("label.player.notes")}
						</label>
						<textarea
							id="notes"
							value={notesInput}
							onChange={(e) => setNotesInput(e.target.value)}
							placeholder={_("admin.duplicate_page.notes_placeholder")}
							rows={4}
							style={{
								width: "100%",
								padding: "0.5rem",
								border: "1px solid #ccc",
								borderRadius: "4px",
								fontFamily: "inherit",
								fontSize: "inherit",
							}}
						/>
					</div>
					<div
						style={{
							display: "flex",
							gap: "1rem",
							justifyContent: "flex-end",
							marginTop: "1rem",
						}}
					>
						<Button
							variant="secondary"
							onClick={handleCancelModal}
							className="btn btn--grey"
						>
							{_("btn.cancel")}
						</Button>
						<Button
							variant="primary"
							onClick={handleMarkAsNotDuplicateSubmit}
							className="btn btn--primary"
						>
							{_("admin.duplicate_page.btn.mark_as_not_duplicate")}
						</Button>
					</div>
				</div>
			</Modal>
		</div>
	);
}
